/**
 * QuickLaydownPage — Operator-Mode Bale Selection
 * ─────────────────────────────────────────────────────────────────────────────
 * Tablet-first, blue-collar friendly. 3 taps to a draft laydown:
 *   1. Tap a yarn recipe chip
 *   2. Adjust target bales with +/- (default 60)
 *   3. Tap "Suggest Bales"
 *
 * Then 1 tap to approve. Total: 4 taps from start to printable floor map.
 *
 * Design rules (Sec 26 of blueprint + Phase 1 chunk D-1 design):
 *   - Big touch targets (≥48px)
 *   - No free-text typing for HVI numbers; recipe targets auto-fill
 *   - Traffic-light verdict (🟢/🟡/🔴) instead of dense numeric tables
 *   - All inline; no drawers, modals only for approval confirm
 *   - i18n-ready strings (English defaults; Tamil/Hindi/Marathi later)
 *
 * The PLANT-HEAD/QC view stays at /bale-mgmt/laydown-planning (existing rich
 * page) — that's chunk D-2.
 */
import { useMemo, useState } from 'react'
import {
  App, Button, Card, Col, Modal, Row, Space, Spin, Tag, Typography,
} from 'antd'
import {
  ThunderboltOutlined, CheckCircleFilled,
  ArrowLeftOutlined, MinusOutlined, PlusOutlined, PrinterOutlined,
  ReloadOutlined,
} from '@ant-design/icons'
import { useQuery, useMutation } from '@tanstack/react-query'
import { baleSelectionApi, mixingRecipeApi, multiOptionApi } from '@/features/baleManagement/api'
import OptionComparisonView from '@/shared/components/OptionComparisonView'
import { recipeApi } from '@/features/recipe/api'
import { usePlantId } from '@/shared/hooks/usePlantId'
import PageHeader from '@/shared/components/PageHeader'
import VerdictBadge from '@/shared/components/VerdictBadge'

const { Title, Text } = Typography

/** Format yarn count cleanly: 30.00 → "30s", 32.50 → "32.5s". */
function fmtCount(n: number | null | undefined): string {
  if (n == null) return '?'
  const v = Number(n)
  return Number.isInteger(v) ? `${v}s` : `${v}s`
}

// ── Big-button defaults — operator-friendly sizing ───────────────────────────
const CHIP_BTN: React.CSSProperties = {
  height: 64,
  minWidth: 130,
  fontSize: 17,
  fontWeight: 600,
  borderRadius: 12,
}
const CHIP_BTN_SELECTED: React.CSSProperties = {
  ...CHIP_BTN,
  background: '#1677ff',
  color: 'white',
  borderColor: '#1677ff',
  boxShadow: '0 4px 12px rgba(22,119,255,0.35)',
}
const NUMBER_BTN: React.CSSProperties = {
  height: 80,
  width: 80,
  fontSize: 32,
  borderRadius: 16,
}
const HUGE_CTA: React.CSSProperties = {
  height: 88,
  fontSize: 22,
  fontWeight: 700,
  borderRadius: 16,
  marginTop: 24,
  boxShadow: '0 6px 18px rgba(22,119,255,0.4)',
}

// ── Main component ───────────────────────────────────────────────────────────
export default function QuickLaydownPage() {
  const { message } = App.useApp()
  const { plantId } = usePlantId()

  const [recipeId, setRecipeId] = useState<string | null>(null)
  const [tierId, setTierId] = useState<string | null>(null)
  const [targetBales, setTargetBales] = useState<number>(60)
  const [activeRunId, setActiveRunId] = useState<string | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  // Sprint E — selection mode toggle: 'matrix' = HVI proximity (default),
  //   'recipe' = grade-percentage recipe (Sec 4)
  const [mode, setMode] = useState<'matrix' | 'recipe'>('matrix')
  // Phase 3B-2: 3-option comparison (Quality / Cost / Balanced)
  const [optionsView, setOptionsView] = useState<{
    quality: any; cost: any; balanced: any
  } | null>(null)
  const [pickingMode, setPickingMode] = useState<'quality' | 'cost' | 'balanced' | null>(null)

  // Recipe list (chips) — show ALL recipes for this plant. The
  // strict is_active filter previously used here was masking the
  // common "no approved recipe yet" case as "the picker is broken".
  const { data: recipes = [], isLoading: recipesLoading } = useQuery({
    queryKey: ['recipes-active', plantId],
    queryFn: () => recipeApi.list({ plant_id: plantId }),
    enabled: !!plantId,
  })

  // Quality tiers (chips) — auto-defaults to COMFORTABLE
  const { data: tiers = [] } = useQuery({
    queryKey: ['quality-tiers', plantId],
    queryFn: () => baleSelectionApi.listTiers(plantId as string),
    enabled: !!plantId,
  })

  // Default tier on load
  useMemo(() => {
    if (!tierId && tiers.length > 0) {
      const comfortable = tiers.find((t: any) => t.tier_code === 'COMFORTABLE') || tiers[0]
      setTierId(comfortable.id)
    }
  }, [tiers, tierId])

  // Fetch the currently-active run (after suggest)
  const { data: activeRun, isLoading: runLoading, refetch: refetchRun } = useQuery({
    queryKey: ['bale-selection-run', activeRunId],
    queryFn: () => baleSelectionApi.getRun(activeRunId as string),
    enabled: !!activeRunId,
  })

  // Suggest mutation — branches on mode (HVI matrix vs recipe-driven)
  const suggestMut = useMutation({
    mutationFn: () => {
      const common = {
        plant_id: plantId as string,
        yarn_recipe_id: recipeId as string,
        quality_tier_id: tierId as string,
        target_bales: targetBales,
      }
      if (mode === 'recipe') {
        return mixingRecipeApi.suggestByRecipe(common)
      }
      return baleSelectionApi.suggest({
        ...common,
        properties: ['sci', 'mic'],
        grouping_count: 5,
      })
    },
    onSuccess: (run) => {
      setActiveRunId(run.id)
      message.success(
        mode === 'recipe'
          ? 'Recipe-driven laydown suggested. Review and approve.'
          : 'HVI-matrix laydown suggested. Review and approve.',
      )
    },
    onError: (e: any) => message.error(e?.response?.data?.detail || 'Could not suggest bales'),
  })

  // Phase 3B-2: 3-option preview (Quality / Cost / Balanced) without persist.
  const compareMut = useMutation({
    mutationFn: () => multiOptionApi.suggestOptions({
      plant_id: plantId as string,
      yarn_recipe_id: recipeId as string,
      quality_tier_id: tierId as string,
      target_bales: targetBales,
    }),
    onSuccess: (data) => {
      setOptionsView(data)
      message.success('Compare and pick the best option for this run.')
    },
    onError: (e: any) => message.error(e?.response?.data?.detail || 'Could not generate options'),
  })

  // After operator picks one of the 3 options, save it via the regular
  // suggest endpoint with mode=<chosen> + persist=true.
  const pickOption = (chosen: 'quality' | 'cost' | 'balanced') => {
    setPickingMode(chosen)
    baleSelectionApi.suggest({
      plant_id: plantId as string,
      yarn_recipe_id: recipeId as string,
      quality_tier_id: tierId as string,
      target_bales: targetBales,
      properties: ['sci', 'mic'],
      grouping_count: 5,
      mode: chosen,
    }).then((run) => {
      setActiveRunId(run.id)
      setOptionsView(null)
      setPickingMode(null)
      message.success(`Saved ${chosen} option as DRAFT. Review and approve.`)
    }).catch((e: any) => {
      setPickingMode(null)
      message.error(e?.response?.data?.detail || 'Could not save option')
    })
  }

  // Approve mutation
  const approveMut = useMutation({
    mutationFn: (override_reason?: string) =>
      baleSelectionApi.approve(activeRunId as string, override_reason ? { override_reason } : {}),
    onSuccess: () => {
      message.success('Approved. Print floor map next.')
      refetchRun()
      setConfirmOpen(false)
    },
    onError: (e: any) => message.error(e?.response?.data?.detail || 'Approval failed'),
  })

  // Reset to fresh state
  const reset = () => {
    setActiveRunId(null)
    setTargetBales(60)
  }

  // ── Render: result view ───────────────────────────────────────────────────
  // ── Phase 3B-2: 3-option comparison view ─────────────────────────────────
  if (optionsView) {
    return (
      <div style={{ padding: 16, maxWidth: 1200, margin: '0 auto' }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => setOptionsView(null)}
          style={{ marginBottom: 16 }}
        >
          Back to settings
        </Button>
        <Title level={4} style={{ margin: 0, marginBottom: 4 }}>
          Pick the best option for {targetBales} bales
        </Title>
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Compare quality, cost, and the balanced middle ground. Tap one to save it as DRAFT.
        </Text>
        <OptionComparisonView
          options={optionsView}
          onPick={pickOption}
          picking={pickingMode}
        />
      </div>
    )
  }

  if (activeRunId && activeRun) {
    const drift = activeRun.drift_status
    const wv = activeRun.within_variance_json || {}
    const isApproved = activeRun.status === 'APPROVED'
    const driftBlocked = drift === 'EXCEEDS_LIMITS'
    const baleCount = (activeRun.selected_bale_ids || []).length

    return (
      <div style={{ padding: 16, maxWidth: 720, margin: '0 auto' }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={reset}
          style={{ marginBottom: 16, height: 48, fontSize: 16 }}
        >
          Start Over
        </Button>

        <VerdictBadge
          band={activeRun.within_band}
          baleCount={baleCount}
        />

        {/* Quick stats — 3 big tiles */}
        <Row gutter={12} style={{ marginBottom: 16 }}>
          <Col xs={8}>
            <Card styles={{ body: { padding: 16, textAlign: 'center' } }}>
              <Text type="secondary" style={{ fontSize: 13 }}>Avg Mic</Text>
              <div style={{ fontSize: 28, fontWeight: 700 }}>
                {wv.mic?.mean?.toFixed?.(2) ?? '—'}
              </div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                CV {wv.mic?.cv_pct?.toFixed?.(1) ?? '—'}%
              </Text>
            </Card>
          </Col>
          <Col xs={8}>
            <Card styles={{ body: { padding: 16, textAlign: 'center' } }}>
              <Text type="secondary" style={{ fontSize: 13 }}>Avg SCI</Text>
              <div style={{ fontSize: 28, fontWeight: 700 }}>
                {wv.sci?.mean?.toFixed?.(0) ?? '—'}
              </div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                CV {wv.sci?.cv_pct?.toFixed?.(1) ?? '—'}%
              </Text>
            </Card>
          </Col>
          <Col xs={8}>
            <Card styles={{ body: { padding: 16, textAlign: 'center' } }}>
              <Text type="secondary" style={{ fontSize: 13 }}>Drift</Text>
              <div style={{
                fontSize: 22, fontWeight: 700,
                color: drift === 'EXCEEDS_LIMITS' ? '#ff4d4f'
                     : drift === 'WITHIN_LIMITS' ? '#52c41a'
                     : '#8c8c8c',
              }}>
                {drift === 'NO_PRIOR' ? 'First' : drift === 'WITHIN_LIMITS' ? 'OK' : 'Over'}
              </div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {drift === 'NO_PRIOR' ? 'No prior laydown' : 'vs last laydown'}
              </Text>
            </Card>
          </Col>
        </Row>

        {/* Warnings */}
        {(activeRun.warnings || []).length > 0 && (
          <Card
            style={{ background: '#fffbe6', borderColor: '#faad14', marginBottom: 16 }}
            styles={{ body: { padding: 16 } }}
          >
            <Text strong style={{ color: '#faad14', fontSize: 16 }}>Warnings</Text>
            <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
              {activeRun.warnings.map((w: string, i: number) => (
                <li key={i} style={{ fontSize: 14 }}>{w}</li>
              ))}
            </ul>
          </Card>
        )}

        {/* Action buttons */}
        {!isApproved ? (
          <Space direction="vertical" style={{ width: '100%' }} size={12}>
            <Button
              type="primary"
              icon={<CheckCircleFilled />}
              block
              style={{ ...HUGE_CTA, marginTop: 0 }}
              danger={driftBlocked}
              disabled={baleCount === 0}
              onClick={() => setConfirmOpen(true)}
              loading={approveMut.isPending}
            >
              {baleCount === 0 ? 'No Bales to Approve'
               : driftBlocked ? 'Approve with Override'
               : 'Approve & Print Map'}
            </Button>
            <Button
              block
              icon={<ReloadOutlined />}
              style={{ height: 56, fontSize: 17, borderRadius: 12 }}
              onClick={() => suggestMut.mutate()}
              loading={suggestMut.isPending}
            >
              Try Different Mix
            </Button>
          </Space>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size={12}>
            <Card
              style={{ background: '#f6ffed', borderColor: '#52c41a' }}
              styles={{ body: { padding: 20, textAlign: 'center' } }}
            >
              <CheckCircleFilled style={{ fontSize: 40, color: '#52c41a' }} />
              <div style={{ fontSize: 22, fontWeight: 700, marginTop: 8 }}>Approved</div>
              <Text type="secondary">Ready to take to floor</Text>
            </Card>
            <Button
              type="primary"
              block
              icon={<PrinterOutlined />}
              style={{ ...HUGE_CTA, marginTop: 0 }}
              onClick={() => window.print()}
            >
              Print Floor Map
            </Button>
            <Button block style={{ height: 56, fontSize: 17, borderRadius: 12 }} onClick={reset}>
              New Laydown
            </Button>
          </Space>
        )}

        {/* Approve modal — handles override reason if drift exceeds */}
        <Modal
          open={confirmOpen}
          title={driftBlocked ? 'Drift exceeds limit' : 'Approve laydown?'}
          onOk={() => approveMut.mutate(driftBlocked ? 'Operator override (floor)' : undefined)}
          onCancel={() => setConfirmOpen(false)}
          okText={driftBlocked ? 'Override and Approve' : 'Yes, Approve'}
          cancelText="Cancel"
          okButtonProps={{ danger: driftBlocked, size: 'large' }}
          cancelButtonProps={{ size: 'large' }}
        >
          {driftBlocked ? (
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text type="warning" style={{ fontSize: 15 }}>
                This laydown drifts more than allowed from the previous one.
                Approving anyway needs supervisor sign-off.
              </Text>
              <Text type="secondary">A note will be saved in the audit log.</Text>
            </Space>
          ) : (
            <Text style={{ fontSize: 15 }}>
              {(activeRun.selected_bale_ids || []).length} bales will be reserved
              for this laydown. You can still substitute individual bales after approval.
            </Text>
          )}
        </Modal>
      </div>
    )
  }

  // ── Loading state for active run ───────────────────────────────────────────
  if (activeRunId && runLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    )
  }

  // ── Render: input view (3 steps) ───────────────────────────────────────────
  return (
    <div style={{ padding: 16, maxWidth: 720, margin: '0 auto' }}>
      <PageHeader
        title="Quick Laydown"
        subtitle="Pick the count, set bale count, hit suggest. Floor mode."
      />

      <Card style={{ marginBottom: 16, borderRadius: 12, background: '#f6ffed',
                     borderColor: '#b7eb8f' }} styles={{ body: { padding: 12 } }}>
        <Text style={{ fontSize: 12 }}>
          <strong>Use this page if:</strong> you&apos;re a floor operator on a tablet
          and want a fast 3-tap laydown. For full QC controls (bale-level
          substitution, multi-property matrix, blowroom-line filter), use{' '}
          <strong>Laydown Planning</strong> instead.
        </Text>
      </Card>

      {/* Step 1 — Pick recipe */}
      <Card style={{ marginBottom: 16, borderRadius: 12 }} styles={{ body: { padding: 20 } }}>
        <Title level={5} style={{ marginTop: 0, marginBottom: 12 }}>
          1. Pick the count
        </Title>
        {recipesLoading ? (
          <Spin />
        ) : recipes.length === 0 ? (
          <Text type="secondary">No active yarn recipes for this plant. Add one in Recipes first.</Text>
        ) : (
          <Space wrap size={12}>
            {recipes.map((r: any) => {
              const selected = recipeId === r.id
              return (
                <Button
                  key={r.id}
                  style={selected ? CHIP_BTN_SELECTED : CHIP_BTN}
                  onClick={() => setRecipeId(r.id)}
                >
                  {fmtCount(r.count_ne)} {r.process_type?.replace('_', ' ')}
                </Button>
              )
            })}
          </Space>
        )}
      </Card>

      {/* Step 2 — Bale count */}
      <Card style={{ marginBottom: 16, borderRadius: 12 }} styles={{ body: { padding: 20 } }}>
        <Title level={5} style={{ marginTop: 0, marginBottom: 12 }}>
          2. How many bales?
        </Title>
        <Space size={24} align="center" style={{ width: '100%', justifyContent: 'center' }}>
          <Button
            icon={<MinusOutlined style={{ fontSize: 24 }} />}
            style={NUMBER_BTN}
            disabled={targetBales <= 6}
            onClick={() => setTargetBales(t => Math.max(6, t - 6))}
          />
          <div style={{ minWidth: 120, textAlign: 'center' }}>
            <div style={{ fontSize: 56, fontWeight: 800, lineHeight: 1 }}>
              {targetBales}
            </div>
            <Text type="secondary" style={{ fontSize: 13 }}>bales</Text>
          </div>
          <Button
            icon={<PlusOutlined style={{ fontSize: 24 }} />}
            style={NUMBER_BTN}
            disabled={targetBales >= 240}
            onClick={() => setTargetBales(t => Math.min(240, t + 6))}
          />
        </Space>
        <div style={{ textAlign: 'center', marginTop: 8 }}>
          <Tag color="default">Uster recommends 35–80 per laydown</Tag>
        </div>
      </Card>

      {/* Step 3 — Quality tier (optional override; default COMFORTABLE) */}
      <Card style={{ marginBottom: 16, borderRadius: 12 }} styles={{ body: { padding: 20 } }}>
        <Title level={5} style={{ marginTop: 0, marginBottom: 12 }}>
          3. Quality preset
        </Title>
        <Space wrap size={12}>
          {tiers.map((t: any) => {
            const selected = tierId === t.id
            const label = t.tier_code === 'COMFORTABLE' ? 'Standard'
                        : t.tier_code === 'PREMIUM_EXPORT' ? 'Premium'
                        : t.tier_code === 'COARSE' ? 'Coarse'
                        : t.tier_name
            return (
              <Button
                key={t.id}
                style={selected ? CHIP_BTN_SELECTED : CHIP_BTN}
                onClick={() => setTierId(t.id)}
              >
                {label}
              </Button>
            )
          })}
        </Space>
      </Card>

      {/* Sprint E — Selection mode toggle (Sec 7 alt-mode) */}
      <Card style={{ marginBottom: 16, borderRadius: 12 }} styles={{ body: { padding: 16 } }}>
        <Title level={5} style={{ margin: 0, marginBottom: 8 }}>
          4. Selection mode
        </Title>
        <Space wrap size={12}>
          <Button
            style={mode === 'matrix' ? CHIP_BTN_SELECTED : CHIP_BTN}
            onClick={() => setMode('matrix')}
          >
            HVI Matrix
          </Button>
          <Button
            style={mode === 'recipe' ? CHIP_BTN_SELECTED : CHIP_BTN}
            onClick={() => setMode('recipe')}
          >
            Recipe-driven
          </Button>
        </Space>
        <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
          {mode === 'matrix'
            ? 'Picks bales by Mic/SCI proximity (statistical consistency).'
            : 'Picks bales matching the prescribed grade % from Mixing Recipe Master (Sec 4).'}
        </div>
      </Card>

      {/* Suggest CTA */}
      <Button
        type="primary"
        icon={<ThunderboltOutlined style={{ fontSize: 28 }} />}
        block
        style={HUGE_CTA}
        disabled={!recipeId || !tierId || !plantId}
        loading={suggestMut.isPending}
        onClick={() => suggestMut.mutate()}
      >
        SUGGEST {targetBales} BALES {mode === 'recipe' ? '(RECIPE)' : ''}
      </Button>

      {/* Phase 3B-2: secondary CTA — show 3 options for comparison */}
      {mode === 'matrix' && (
        <Button
          block
          size="large"
          style={{ marginTop: 8, height: 48, fontSize: 14, fontWeight: 600 }}
          disabled={!recipeId || !tierId || !plantId}
          loading={compareMut.isPending}
          onClick={() => compareMut.mutate()}
        >
          🔄 Compare 3 Options (Quality / Cost / Balanced)
        </Button>
      )}
    </div>
  )
}
